import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '@/context/LanguageContext';

export default function PrivacyPolicy() {
  const { lang } = useLanguage();

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{lang === 'en' ? 'Privacy Policy' : 'Informativa sulla Privacy'} | Officina Martorana</title>
      </Helmet>
      <Navbar />

      <div className="pt-32 pb-20 px-4 sm:px-6 lg:px-8" data-testid="privacy-policy-page">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Space Grotesk' }}>
            Informativa sulla Privacy
          </h1>
          <p className="text-sm text-gray-500 mb-12">Ultimo aggiornamento: marzo 2026</p>

          <div className="prose prose-lg max-w-none text-gray-700 space-y-8">

            {/* 1. Titolare del trattamento */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk' }}>1. Titolare del Trattamento</h2>
              <p>
                Il Titolare del trattamento dei dati personali raccolti tramite il presente sito web è:
              </p>
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 not-prose">
                <p className="font-bold text-gray-900">Officina Martorana Bosch Car Service S.r.l.</p>
                <p className="text-gray-600 mt-1">Via Pompei 7, 92019 Sciacca (AG), Italia</p>
                <p className="text-gray-600">P.IVA: [da inserire]</p>
                <p className="text-gray-600">Email: <a href="mailto:officinamartorana@gmail.com" className="text-[#0066b2] hover:underline">officinamartorana@gmail.com</a></p>
                <p className="text-gray-600">Telefono: <a href="tel:+390925259520" className="text-[#0066b2] hover:underline">0925 25952</a></p>
                <p className="text-gray-600">WhatsApp: <a href="https://wa.me/393939049492" className="text-[#0066b2] hover:underline">+39 393 904 9492</a></p>
              </div>
              <p>
                Per qualsiasi richiesta relativa alla protezione dei dati personali, è possibile contattare il Titolare all'indirizzo email sopra indicato, specificando nell'oggetto "Privacy – Esercizio dei diritti".
              </p>
            </section>

            {/* 2. Tipologie di dati raccolti */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk' }}>2. Tipologie di Dati Personali Raccolti</h2>
              <p>Il sito web raccoglie, direttamente o tramite terze parti, le seguenti categorie di dati personali:</p>
              
              <h3 className="text-xl font-semibold text-gray-900 mt-4">a) Dati anagrafici e di contatto</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li>Nome e cognome</li>
                <li>Indirizzo email</li>
                <li>Numero di telefono e/o WhatsApp</li>
                <li>Indirizzo di residenza o domicilio (ove fornito)</li>
                <li>Città di provenienza</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mt-4">b) Dati relativi al veicolo</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li>Targa del veicolo</li>
                <li>Modello e marca dell'automobile</li>
                <li>Storico degli interventi effettuati</li>
                <li>Preferenze e necessità di manutenzione</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mt-4">c) Dati tecnici e di navigazione</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li>Indirizzo IP</li>
                <li>Tipo di browser e sistema operativo</li>
                <li>Pagine visitate e tempo di permanenza</li>
                <li>Dati di interazione con il sito (click, scroll, navigazione)</li>
                <li>Dati raccolti tramite cookie e tecnologie simili</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mt-4">d) Dati forniti volontariamente</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li>Messaggi inviati tramite il modulo di contatto</li>
                <li>Richieste di preventivo</li>
                <li>Prenotazioni di interventi tramite il calendario interattivo</li>
                <li>Conversazioni con l'assistente AI integrato nel sito</li>
                <li>Comunicazioni via WhatsApp Business</li>
              </ul>
            </section>

            {/* 3. Finalità e base giuridica */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk' }}>3. Finalità e Base Giuridica del Trattamento</h2>
              <p>I dati personali sono trattati per le seguenti finalità:</p>

              <div className="overflow-x-auto mt-4">
                <table className="min-w-full border border-gray-200 rounded-xl overflow-hidden not-prose text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-gray-900 border-b">Finalità</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-900 border-b">Base giuridica</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr><td className="px-4 py-3">Erogazione dei servizi richiesti (prenotazioni, assistenza, riparazioni)</td><td className="px-4 py-3">Esecuzione di un contratto o misure precontrattuali (Art. 6.1.b GDPR)</td></tr>
                    <tr><td className="px-4 py-3">Risposta a richieste di contatto e preventivo</td><td className="px-4 py-3">Legittimo interesse del Titolare (Art. 6.1.f GDPR)</td></tr>
                    <tr><td className="px-4 py-3">Gestione amministrativa e contabile</td><td className="px-4 py-3">Obbligo legale (Art. 6.1.c GDPR)</td></tr>
                    <tr><td className="px-4 py-3">Invio di comunicazioni promozionali, offerte e campagne marketing</td><td className="px-4 py-3">Consenso dell'interessato (Art. 6.1.a GDPR)</td></tr>
                    <tr><td className="px-4 py-3">Analisi statistiche e miglioramento del servizio</td><td className="px-4 py-3">Legittimo interesse del Titolare (Art. 6.1.f GDPR)</td></tr>
                    <tr><td className="px-4 py-3">Funzionamento dell'assistente AI e personalizzazione dell'esperienza</td><td className="px-4 py-3">Consenso dell'interessato (Art. 6.1.a GDPR)</td></tr>
                    <tr><td className="px-4 py-3">Invio di notifiche push relative a prenotazioni e aggiornamenti</td><td className="px-4 py-3">Consenso dell'interessato (Art. 6.1.a GDPR)</td></tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* 4. Modalità di trattamento */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk' }}>4. Modalità di Trattamento e Conservazione</h2>
              <p>
                I dati personali sono trattati con strumenti elettronici e automatizzati, adottando misure di sicurezza tecniche e organizzative adeguate a garantirne la riservatezza, l'integrità e la disponibilità, nel rispetto di quanto previsto dall'Art. 32 del GDPR.
              </p>
              <p>In particolare:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>I dati sono conservati su server protetti con crittografia e accesso limitato al personale autorizzato.</li>
                <li>Le conversazioni con l'assistente AI sono trattate in forma anonimizzata ove possibile e non vengono utilizzate per finalità diverse dall'assistenza al cliente.</li>
                <li>I database dei clienti sono protetti e accessibili esclusivamente dal Titolare e dal personale autorizzato.</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mt-4">Periodo di conservazione</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Dati contrattuali e contabili:</strong> conservati per 10 anni dalla cessazione del rapporto, come previsto dalla normativa fiscale italiana.</li>
                <li><strong>Dati di contatto per marketing:</strong> conservati fino alla revoca del consenso da parte dell'interessato.</li>
                <li><strong>Dati di navigazione e cookie:</strong> conservati secondo le tempistiche indicate nella Cookie Policy (max 13 mesi per cookie analitici).</li>
                <li><strong>Dati delle prenotazioni:</strong> conservati per 5 anni ai fini di garanzia e storico interventi.</li>
                <li><strong>Conversazioni AI:</strong> conservate per 12 mesi, poi anonimizzate o cancellate.</li>
              </ul>
            </section>

            {/* 5. Comunicazione a terzi */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk' }}>5. Comunicazione e Trasferimento dei Dati</h2>
              <p>I dati personali possono essere comunicati a:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Fornitori di servizi tecnologici</strong> che agiscono in qualità di Responsabili del trattamento, tra cui: provider di hosting, servizi cloud, piattaforme di analisi e marketing.</li>
                <li><strong>Partner della rete Bosch Car Service</strong>, limitatamente a quanto necessario per l'erogazione dei servizi.</li>
                <li><strong>Autorità competenti</strong>, ove previsto dalla legge.</li>
              </ul>
              <p className="mt-4">I principali fornitori terzi utilizzati dal sito includono:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>Google LLC</strong> (Analytics, Maps, Tag Manager) – <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#0066b2] hover:underline">Privacy Policy Google</a></li>
                <li><strong>Meta Platforms, Inc.</strong> (Facebook, Instagram) – <a href="https://www.facebook.com/privacy/policy" target="_blank" rel="noopener noreferrer" className="text-[#0066b2] hover:underline">Privacy Policy Meta</a></li>
                <li><strong>WhatsApp LLC</strong> (WhatsApp Business) – <a href="https://www.whatsapp.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-[#0066b2] hover:underline">Privacy Policy WhatsApp</a></li>
                <li><strong>OpenAI</strong> (Assistente AI) – <a href="https://openai.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#0066b2] hover:underline">Privacy Policy OpenAI</a></li>
              </ul>
              <p className="mt-4">
                Alcuni dei suddetti fornitori potrebbero trasferire dati al di fuori dello Spazio Economico Europeo (SEE). In tal caso, il trasferimento avviene sulla base di garanzie adeguate ai sensi degli Artt. 46-49 del GDPR, incluse le Clausole Contrattuali Standard approvate dalla Commissione Europea.
              </p>
            </section>

            {/* 6. Sistemi automatizzati */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk' }}>6. Processi Decisionali Automatizzati e Profilazione</h2>
              <p>
                Il sito utilizza un assistente virtuale basato su intelligenza artificiale (AI) per fornire informazioni sui servizi, assistere nelle prenotazioni e rispondere a domande frequenti. Le risposte generate dall'AI sono di natura informativa e non comportano decisioni automatizzate che producano effetti giuridici significativi sull'interessato.
              </p>
              <p>
                Le attività di marketing e invio di comunicazioni promozionali possono avvalersi di sistemi automatizzati per la segmentazione del pubblico e la personalizzazione dei contenuti, sempre sulla base del consenso preventivo dell'utente.
              </p>
            </section>

            {/* 7. Diritti dell'utente */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk' }}>7. Diritti dell'Interessato</h2>
              <p>Ai sensi degli Artt. 15-22 del GDPR, l'interessato ha diritto di:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Diritto di accesso (Art. 15):</strong> ottenere conferma che sia o meno in corso un trattamento dei propri dati e, in tal caso, ottenerne copia.</li>
                <li><strong>Diritto di rettifica (Art. 16):</strong> ottenere la correzione di dati personali inesatti o l'integrazione di dati incompleti.</li>
                <li><strong>Diritto alla cancellazione (Art. 17):</strong> ottenere la cancellazione dei propri dati personali ("diritto all'oblio"), nei limiti e alle condizioni previste dalla legge.</li>
                <li><strong>Diritto di limitazione (Art. 18):</strong> ottenere la limitazione del trattamento quando ricorrono determinate condizioni.</li>
                <li><strong>Diritto alla portabilità (Art. 20):</strong> ricevere i propri dati in formato strutturato, di uso comune e leggibile da dispositivo automatico.</li>
                <li><strong>Diritto di opposizione (Art. 21):</strong> opporsi in qualsiasi momento al trattamento dei propri dati per finalità di marketing diretto.</li>
                <li><strong>Diritto di revoca del consenso (Art. 7):</strong> revocare in qualsiasi momento il consenso prestato, senza pregiudicare la liceità del trattamento basato sul consenso prima della revoca.</li>
              </ul>
              <p className="mt-4">
                Per esercitare i propri diritti, l'interessato può inviare una richiesta scritta a: <a href="mailto:officinamartorana@gmail.com" className="text-[#0066b2] hover:underline font-medium">officinamartorana@gmail.com</a>, specificando nell'oggetto "Privacy – Esercizio dei diritti".
              </p>
              <p>
                L'interessato ha inoltre il diritto di proporre reclamo all'Autorità Garante per la Protezione dei Dati Personali (<a href="https://www.garanteprivacy.it" target="_blank" rel="noopener noreferrer" className="text-[#0066b2] hover:underline">www.garanteprivacy.it</a>).
              </p>
            </section>

            {/* 8. Aggiornamento */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk' }}>8. Modifiche alla Presente Informativa</h2>
              <p>
                Il Titolare si riserva il diritto di modificare o aggiornare la presente informativa in qualsiasi momento. Le modifiche saranno pubblicate su questa pagina con indicazione della data di ultimo aggiornamento. Si consiglia di consultare periodicamente questa pagina per verificare eventuali variazioni.
              </p>
              <p>
                La presente informativa è soggetta a revisione annuale o in occasione di modifiche normative, organizzative o tecnologiche rilevanti.
              </p>
            </section>

          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
